import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  useMap,
  AdvancedMarker,
  InfoWindow,
  Pin,
} from "@vis.gl/react-google-maps";
import { useState, useRef, useEffect } from "react";
import { MarkedLocation } from "../MapView/MapView";
import type { Marker } from "@googlemaps/markerclusterer";

interface PoiMarkersProps {
  pois: MarkedLocation[];
  ToolTipComponent: React.FC<{ selectedDataIndex?: number }>;
  ToolTipHeader: React.FC<{ selectedDataIndex: number }>;
}

const MapCustomMarkers = ({
  pois,
  ToolTipComponent,
  ToolTipHeader,
}: PoiMarkersProps) => {
  const map = useMap();
  const [markers, setMarkers] = useState<{ [key: string]: google.maps.marker.AdvancedMarkerElement; }>({});
  const markerKeysRef = useRef<Map<Marker, string>>(new Map());
  const clusterer = useRef<MarkerClusterer | null>(null);
  const [selectedPoi, setSelectedPoi] = useState<MarkedLocation | null>(null);
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number>(-1);
  const prevPoisRef = useRef<MarkedLocation[]>([]);


  useEffect(() => {
    if (!map) return;

    if (clusterer.current) {
      clusterer.current.clearMarkers();
    }

    clusterer.current = new MarkerClusterer({
      map,
      renderer: {
        render: (cluster) => {
          const clusterMarkers = cluster.markers;
          const hasCheckedMarker = clusterMarkers.some((marker: any) => {
            const poiKey = markerKeysRef.current.get(marker);
            const poi = pois.find(p => p.key === poiKey);
            return poi?.checked;
          });

          const clusterColor = hasCheckedMarker ? '#0000aa' : 'gray';

          return new google.maps.Marker({
            position: cluster.position,
            label: {
              text: String(cluster.count),
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 15,
              fillColor: clusterColor,
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
            title: String(cluster.count),
            zIndex: Math.floor(cluster.position.lat()) * 1000000 + Math.floor(cluster.position.lng()),
          });
        },
      },
    });


    clusterer.current.addMarkers(Object.values(markers));
  }, [map, pois, markers]);

  useEffect(() => {
    if (!clusterer.current || !map) return;
    clusterer.current.clearMarkers();
    clusterer.current.addMarkers(Object.values(markers));
  }, [markers, map]);

  useEffect(() => {
    if (!map) return;

    const prevPois = prevPoisRef.current;

    let newlyChecked: MarkedLocation | null = null;

    for (let i = 0; i < pois.length; i++) {
      if (pois[i].checked && !prevPois[i]?.checked) {
        newlyChecked = pois[i];
        break;
      }
    }

    if (!newlyChecked) {
      prevPoisRef.current = pois;
      return;
    }

    setSelectedPoi(null);
    setSelectedPoiIndex(-1);

    const marker = markers[newlyChecked.key];
    if (!marker) {
      prevPoisRef.current = pois;
      return;
    }

    let targetPosition = newlyChecked.location;

    if (!marker.map && marker.position) {
      targetPosition = new google.maps.LatLng(marker.position).toJSON();
    }

    map.panTo(targetPosition);

    prevPoisRef.current = pois;

  }, [pois, markers, map]);




  const setMarkerRef = (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;
    setMarkers((prev) => {
      if (marker) {
        markerKeysRef.current.set(marker, key);
        return { ...prev, [key]: marker };
      } else {
        const markerToDelete = markers[key];
        markerKeysRef.current.delete(markerToDelete);
        const newMarkers = { ...prev };
        delete newMarkers[key];
        return newMarkers;
      }
    });
  };

  const handleMarkerClick = (poi: MarkedLocation, dataIndex: number) => {
    setSelectedPoi(poi);
    setSelectedPoiIndex(dataIndex);
  };

  return (
    <>
      {pois.map((poi, index) => {
        const iconUrl = poi?.iconUrl;
        return (
          <AdvancedMarker
            key={poi.key}
            position={poi.location}
            ref={(marker) => setMarkerRef(marker, poi.key)}
            clickable={true}
            onClick={() => handleMarkerClick(poi, index)}
            zIndex={poi.checked ? 1000 : undefined}
          >
            {iconUrl ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <div
                  style={{
                    width: '54px',
                    height: '54px',
                    backgroundColor: '#fff',
                    border: `7px solid ${poi.checked ? '#0000aa' : 'gray'}`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 6px rgba(0,0,0,0.3)',
                  }}
                >
                  <img
                    src={iconUrl as string}
                    alt="machine"
                    style={{
                      width: "70%",
                      height: "70%",
                      objectFit: "contain",
                    }}
                  />
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "-8px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: '21px solid transparent',
                    borderRight: '21px solid transparent',
                    borderTop: `18px solid ${poi.checked ? '#0000aa' : 'gray'}`,
                    zIndex: -1
                  }}
                />
              </div>
            ) : (
              <Pin
                background={"#fb042dff"}
                glyphColor={"#000"}
                borderColor={"#000"}
              />
            )}
          </AdvancedMarker>
        );
      })}

      {selectedPoi && (
        <InfoWindow
          position={selectedPoi.location}
          onCloseClick={() => {
            setSelectedPoi(null);
            setSelectedPoiIndex(-1);
          }}
          pixelOffset={[0, -40]}
          headerContent={<ToolTipHeader selectedDataIndex={selectedPoiIndex} />}
          style={{ maxWidth: 300, minWidth: 230, minHeight: 150 }}
        >
          <ToolTipComponent selectedDataIndex={selectedPoiIndex} />
        </InfoWindow>
      )}
    </>
  );
};

export default MapCustomMarkers;
